/* eslint-disable jsx-a11y/label-has-associated-control */
import MonthViewCalendar from "components/investorrelations/MonthViewCalendar";
import classNames from "components/ui/classNames";
import useSelfie from "lib/hooks/useSelfie";
import { useId } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/Select";
import { useEventsCalendarValue } from "components/investorrelations/hooks/hooks";
import { IIssuerType } from "types/graphqlTypes";
import CalendarNotes from "./CalendarNotes";
import { EventsCalendarFilterRange, EventsCalenderFilterEventType } from "./enums";
import { getFilterValuesBySelectOption } from "./utils";

export enum RightSideFiltersEnum {
  MyPositions = "MY_POSITIONS",
  IsInBookmarks = "IS_IN_BOOKMARKS",
  isInSP500 = "IS_IN_SP500",
  EventType = "EVENT_TYPE",
  FilterRange = "FILTER_RANGE",
  MonthViewCalendar = "MONTH_VIEW_CALENDAR",
}

interface RightSideFiltersProps {
  className?: string;
  myPositions: boolean;
  isInSP500: boolean;
  isInBookmarks: boolean;
  eventType: EventsCalenderFilterEventType | null;
  issuerType: IIssuerType | null;
  period: EventsCalendarFilterRange;
  calendarInterval: {
    startDate: Date;
    endDate: Date;
  };
  enableFilters: RightSideFiltersEnum[];

  setMyPositions: (value: boolean) => void;
  setIsInSP500: (value: boolean) => void;
  setIsInBookmarks: (value: boolean) => void;
  setEventType: (value: EventsCalenderFilterEventType | null) => void;
  setPeriod: (value: EventsCalendarFilterRange) => void;
  setCalendarInterval: (value: { startDate: Date; endDate: Date }) => void;
}

export default function RightSideFilters({
  className,
  myPositions,
  isInSP500,
  isInBookmarks,
  eventType,
  issuerType,
  period,
  calendarInterval,
  enableFilters,
  setMyPositions,
  setIsInSP500,
  setIsInBookmarks,
  setEventType,
  setPeriod,
  setCalendarInterval,
}: RightSideFiltersProps) {
  const intl = useIntl();
  const selfie = useSelfie();
  const myPositionsCheckboxId = useId();
  const isInSP500CheckboxId = useId();
  const isInBookmarksCheckboxId = useId();
  const data = useEventsCalendarValue();

  function handleSetPeriod(selectedOption: EventsCalendarFilterRange) {
    const { filterType, startDate, endDate } = getFilterValuesBySelectOption(selectedOption);

    setPeriod(filterType);
    setCalendarInterval({
      startDate,
      endDate,
    });
  }

  return (
    <div className={classNames("grid grid-cols-1 gap-4 sm:sticky sm:top-24", className)}>
      {enableFilters.includes(RightSideFiltersEnum.MyPositions) && selfie && (
        <div
          aria-checked={myPositions}
          className={classNames({
            "items-center rounded-md border bg-white p-4 tracking-wide shadow": true,
            "border-violet-300": myPositions,
            "border-transparent": !myPositions,
          })}
        >
          <div className="flex select-none items-center gap-x-2">
            <input
              id={myPositionsCheckboxId}
              type="checkbox"
              name="Minha Carteira"
              className="h-4 w-4 cursor-pointer rounded border-gray-300 text-violet-600 focus:ring-violet-500"
              checked={myPositions}
              onChange={() => setMyPositions(!myPositions)}
            />

            <label
              className={classNames("cursor-pointer text-sm tracking-wider", {
                "font-bold text-violet-500": myPositions,
                "font-medium text-gray-500": !myPositions,
              })}
              htmlFor={myPositionsCheckboxId}
            >
              <FormattedMessage defaultMessage="Minha Carteira" id="6jfnJ3" />
            </label>
          </div>
        </div>
      )}

      {enableFilters.includes(RightSideFiltersEnum.IsInBookmarks) && selfie && (
        <div
          aria-checked={isInBookmarks}
          className={classNames({
            "items-center rounded-md border bg-white p-4 tracking-wide shadow": true,
            "border-violet-300": isInBookmarks,
            "border-transparent": !isInBookmarks,
          })}
        >
          <div className="flex select-none items-center gap-x-2">
            <input
              id={isInBookmarksCheckboxId}
              type="checkbox"
              name="Minha Carteira"
              className="h-4 w-4 cursor-pointer rounded border-gray-300 text-violet-600 focus:ring-violet-500"
              checked={isInBookmarks}
              onChange={() => setIsInBookmarks(!isInBookmarks)}
            />

            <label
              className={classNames("cursor-pointer text-sm tracking-wider", {
                "font-bold text-violet-500": isInBookmarks,
                "font-medium text-gray-500": !isInBookmarks,
              })}
              htmlFor={isInBookmarksCheckboxId}
            >
              <FormattedMessage defaultMessage="Favoritos" id="g2IZZj" />
            </label>
          </div>
        </div>
      )}

      {enableFilters.includes(RightSideFiltersEnum.isInSP500) && issuerType !== IIssuerType.BrazilianCompany && (
        <div
          aria-checked={isInSP500}
          className={classNames({
            "space-y-2 rounded-md border bg-white p-4 tracking-wide shadow": true,
            "border-violet-300": isInSP500,
            "border-transparent": !isInSP500,
          })}
        >
          <div className="flex select-none items-center space-x-2">
            <input
              id={isInSP500CheckboxId}
              type="checkbox"
              name="SEP500"
              className="h-4 w-4 cursor-pointer rounded border-gray-300 text-violet-600 focus:ring-violet-500"
              checked={isInSP500}
              onChange={() => setIsInSP500(!isInSP500)}
            />

            <label
              className={classNames("cursor-pointer text-sm tracking-wider", {
                "font-bold text-violet-500": isInSP500,
                "font-medium text-gray-500": !isInSP500,
              })}
              htmlFor={isInSP500CheckboxId}
            >
              <FormattedMessage defaultMessage="S&P 500" id="p9EKzD" />
            </label>
          </div>
        </div>
      )}

      {enableFilters.includes(RightSideFiltersEnum.EventType) && (
        <Select
          value={eventType || EventsCalenderFilterEventType.All}
          onValueChange={(value) => setEventType(value as EventsCalenderFilterEventType)}
        >
          <SelectTrigger
            className={classNames(
              "font-aeonik",
              "h-12 w-full rounded-lg",
              "border border-slate-200 bg-white",
              "text-sm font-medium tracking-wide text-slate-600",
              "disabled:select-none",
              "hover:border-transparent hover:ring-4 hover:ring-slate-100 hover:ring-offset-1 hover:ring-offset-slate-300",
              "focus-visible:border-transparent focus-visible:ring-4 focus-visible:ring-indigo-100 focus-visible:ring-offset-1 focus-visible:ring-offset-indigo-300",
              "truncate",
            )}
          >
            <SelectValue
              placeholder={intl.formatMessage({
                defaultMessage: "Filtre o tipo do evento",
                id: "puERTR",
              })}
            />
          </SelectTrigger>

          <SelectContent className="rounded-lg border border-slate-200 text-sm" side="top">
            <SelectItem value={EventsCalenderFilterEventType.All}>
              <FormattedMessage defaultMessage="Todos" id="UA87I4" description="Eventos Corporativos" />
            </SelectItem>
            <SelectItem value={EventsCalenderFilterEventType.Earnings}>
              <FormattedMessage defaultMessage="Balanços" id="k+Oqnk" description="Eventos Corporativos" />
            </SelectItem>
            <SelectItem value={EventsCalenderFilterEventType.Dividends}>
              <FormattedMessage defaultMessage="Proventos" id="4UvohG" description="Eventos Corporativos" />
            </SelectItem>
            <SelectItem value={EventsCalenderFilterEventType.StockDividends}>
              <FormattedMessage defaultMessage="Bonificação" id="xEQI3s" description="Eventos Corporativos" />
            </SelectItem>
            <SelectItem value={EventsCalenderFilterEventType.RightsIssue}>
              <FormattedMessage defaultMessage="Subscrição" id="5WMOhr" description="Eventos Corporativos" />
            </SelectItem>
            <SelectItem value={EventsCalenderFilterEventType.StockSplit}>
              <FormattedMessage defaultMessage="Desdobramento" id="SYkm7W" description="Eventos Corporativos" />
            </SelectItem>
            <SelectItem value={EventsCalenderFilterEventType.SpinOff}>
              <FormattedMessage defaultMessage="Cisão" id="xDlFg4" description="Eventos Corporativos" />
            </SelectItem>
          </SelectContent>
        </Select>
      )}

      {enableFilters.includes(RightSideFiltersEnum.FilterRange) && (
        <Select value={period} onValueChange={(value) => handleSetPeriod(value as EventsCalendarFilterRange)}>
          <SelectTrigger
            className={classNames(
              "font-aeonik",
              "h-12 w-full rounded-lg",
              "border border-slate-200 bg-white",
              "text-sm font-medium tracking-wide text-slate-600",
              "disabled:select-none",
              "hover:border-transparent hover:ring-4 hover:ring-slate-100 hover:ring-offset-1 hover:ring-offset-slate-300",
              "focus-visible:border-transparent focus-visible:ring-4 focus-visible:ring-indigo-100 focus-visible:ring-offset-1 focus-visible:ring-offset-indigo-300",
              "truncate",
            )}
          >
            <SelectValue
              placeholder={intl.formatMessage({
                defaultMessage: "Filtre o período do evento",
                id: "HY8UP3",
              })}
            />
          </SelectTrigger>

          <SelectContent className="rounded-lg border border-slate-200 text-sm" side="top">
            <SelectItem value={EventsCalendarFilterRange.Today}>
              <FormattedMessage
                description="Página de eventos (Agenda) - Filtros"
                defaultMessage="Hoje ({count})"
                id="BY2iNs"
                values={{
                  count: data?.agenda.breakdown?.today ?? 0,
                }}
              />
            </SelectItem>
            <SelectItem value={EventsCalendarFilterRange.InTheWeek}>
              <FormattedMessage
                description="Página de eventos (Agenda) - Filtros"
                defaultMessage="Na Semana ({count})"
                id="bT68E6"
                values={{
                  count: data?.agenda.breakdown?.inTheWeek ?? 0,
                }}
              />
            </SelectItem>
            <SelectItem value={EventsCalendarFilterRange.InTheMonth}>
              <FormattedMessage
                description="Página de eventos (Agenda) - Filtros"
                defaultMessage="No Mês ({count})"
                id="lWqHok"
                values={{
                  count: data?.agenda.breakdown?.inTheMonth ?? 0,
                }}
              />
            </SelectItem>
            <SelectItem value={EventsCalendarFilterRange.InTheLastMonth}>
              <FormattedMessage
                description="Página de eventos (Agenda) - Filtros"
                defaultMessage="No Último Mês ({count})"
                id="Kta/aU"
                values={{
                  count: data?.agenda.breakdown?.inTheLastMonth ?? 0,
                }}
              />
            </SelectItem>
            <SelectItem value={EventsCalendarFilterRange.InTheNextMonth}>
              <FormattedMessage
                description="Página de eventos (Agenda) - Filtros"
                defaultMessage="No Próximo Mês ({count})"
                id="V0ZN2j"
                values={{
                  count: data?.agenda.breakdown?.inTheNextMonth ?? 0,
                }}
              />
            </SelectItem>
            <SelectItem value={EventsCalendarFilterRange.InTheNextNinetyDays}>
              <FormattedMessage
                description="Página de eventos (Agenda) - Filtros"
                defaultMessage="Nos Próximos 90D ({count})"
                id="cBNpb1"
                values={{
                  count: data?.agenda.breakdown?.inTheNextNinetyDays ?? 0,
                }}
              />
            </SelectItem>
            <SelectItem value={EventsCalendarFilterRange.InTheYear}>
              <FormattedMessage
                description="Página de eventos (Agenda) - Filtros"
                defaultMessage="Em 12 meses ({count})"
                id="qL53nf"
                values={{
                  count: data?.agenda.breakdown?.inTheYear ?? 0,
                }}
              />
            </SelectItem>
            {period === EventsCalendarFilterRange.InSelectedPeriod && (
              <SelectItem value={EventsCalendarFilterRange.InSelectedPeriod}>
                <FormattedMessage
                  description="Página de eventos (Agenda) - Filtros"
                  defaultMessage="Período Selecionado ({totalEvents})"
                  id="3il2/4"
                  values={{
                    totalEvents: data?.agenda.totalCount ?? 0,
                  }}
                />
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      )}

      {enableFilters.includes(RightSideFiltersEnum.MonthViewCalendar) && (
        <MonthViewCalendar
          startDate={calendarInterval.startDate}
          endDate={calendarInterval.endDate}
          onChange={(range) => {
            setCalendarInterval(range);
            setPeriod(EventsCalendarFilterRange.InSelectedPeriod);
          }}
        />
      )}

      <CalendarNotes />
    </div>
  );
}
